import { decrementRequests, incrementRequests } from 'api/fetcher';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { getBannerCrop } from 'utils/banner/utils';
import { getInputCropTypes, getLoadingCropType } from 'utils/clickme';
import {
    BANNER_IMAGE_CROP_TYPES,
    BANNER_IMAGE_CROP_TYPES_CONFIG,
    BannerImageCropType,
    CROP_CONTAINER_SIZE,
    ImageComponent,
    ImageType,
} from 'utils/constants/banner';
import { MagritteNotificationName } from 'utils/constants/notification';
import Debug from 'utils/debug';
import { getXRequestId } from 'utils/getXRequestId';

import urlParser from 'bloko/common/urlParser';

import { Action } from 'store/createReducer';
import {
    ImageInfoResponse,
    resetBannerImages,
    setBannerImageByType,
    setBannerImageComponentByType,
    setBannerSavedImageByType,
    setIsCropImageModalVisible,
} from 'store/models/createBanner';
import { setIsGalleryModalVisible } from 'store/models/gallery';
import { addMagritteNotification } from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

type LoadImage = (props: {
    imageType: ImageType;
    fileName?: string;
    file?: Blob | null;
    setLoading?: ((isLoading: boolean) => void) | null;
    cropType?: BannerImageCropType | null;
    id?: number | null;
    callBack?: () => void;
    resetBannerImagesOnFail?: boolean;
    onProgress?: (percentage: number) => void;
}) => AppThunkAction;

enum BackendImageError {
    TooHighResolution = 'TOO_HIGH_RESOLUTION',
    TooLowResolution = 'TOO_LOW_RESOLUTION',
    TooLowResolutionForSources = 'TOO_LOW_RESOLUTION_FOR_SOURCES',
    WrongExtension = 'WRONG_EXTENSION',
    UnableToCropImage = 'UNABLE_TO_CROP_IMAGE',
}

interface Error {
    errors: { key: BackendImageError }[];
}

interface RequestData {
    url: string;
    params: Record<string, string | null>;
    data: FormData | null;
    config: AxiosRequestConfig;
}

const ERROR_MAPPING = {
    [BackendImageError.TooHighResolution]: MagritteNotificationName.CampaignFormImageTooHighResolution,
    [BackendImageError.TooLowResolution]: MagritteNotificationName.CampaignFormImageTooLowResolution,
    [BackendImageError.TooLowResolutionForSources]:
        MagritteNotificationName.CampaignFormImageTooLowResolutionForSources,
    [BackendImageError.WrongExtension]: MagritteNotificationName.CampaignFormImageWrongExtension,
    [BackendImageError.UnableToCropImage]: MagritteNotificationName.CampaignFormImageUnableToCropImage,
};

export const loadImage: LoadImage = ({
    imageType,
    fileName,
    file,
    setLoading,
    cropType,
    id,
    callBack,
    resetBannerImagesOnFail = false,
    onProgress,
}) => {
    return (dispatch, getState) => {
        setLoading?.(true);
        const { campaignIdForBanner, images } = getState().createBanner;
        const loadingCropType = cropType ?? getLoadingCropType(images);
        const data = new FormData();
        if (file) {
            data.append('file', file);
        }
        const instance = axios.create();
        instance.defaults.timeout = 0;
        incrementRequests();
        const requestConfig: Record<ImageType, RequestData> = {
            [ImageType.Gallery]: {
                url: '/api/v1/images/gallery/image/duplicate',
                params: { originalImageId: id?.toString() || null, imageCropType: loadingCropType },
                data: null,
                config: {},
            },
            [ImageType.User]: {
                url: '/api/v1/images/upload',
                params: {
                    campaignId: loadingCropType ? null : campaignIdForBanner?.toString() || null,
                    imageCropType: loadingCropType,
                },
                data,
                config: {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: onProgress
                        ? (progressEvent: ProgressEvent) => {
                              if (progressEvent.total) {
                                  const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                  onProgress(percentage);
                              }
                          }
                        : undefined,
                },
            },
        };
        const requestData = requestConfig[imageType];

        return instance
            .post<ImageInfoResponse>(
                `${requestData.url}?${urlParser.stringify(requestData.params)}`,
                requestData.data,
                requestData.config
            )
            .then((response) => {
                const newImage = {
                    ...response.data,
                    fileName,
                    component: ImageComponent.Crop,
                    isLoadedInCrop: false,
                };

                const inputCropTypes = getInputCropTypes(images);
                const currentCropTypes = response.data.availableCropsForImage.filter((imageCropType) =>
                    inputCropTypes.includes(imageCropType)
                );

                if (loadingCropType) {
                    currentCropTypes.push(loadingCropType);
                }

                const actions: Action[] = [];
                currentCropTypes.forEach((cropType) => {
                    if (!BANNER_IMAGE_CROP_TYPES.includes(cropType)) {
                        return;
                    }
                    const { aspect } = BANNER_IMAGE_CROP_TYPES_CONFIG[cropType];

                    const image = {
                        cropType,
                        imageInfo: {
                            ...newImage,
                            cropInfo: getBannerCrop({
                                cropContainerSize: CROP_CONTAINER_SIZE,
                                imageWidth: response.data.width,
                                imageHeight: response.data.height,
                                cropAspect: aspect,
                            }),
                        },
                    };

                    actions.push(setBannerImageByType(image));
                    if (!loadingCropType) {
                        actions.push(setBannerSavedImageByType(image));
                    }
                });

                actions.push(setIsGalleryModalVisible(false));
                setLoading?.(false);
                dispatch([...actions]);
                callBack?.();
            })
            .catch((response: AxiosError<Error>) => {
                dispatch(setIsGalleryModalVisible(false));
                setLoading?.(false);
                if (loadingCropType) {
                    dispatch(
                        setBannerImageComponentByType({
                            cropType: loadingCropType,
                            component: images[loadingCropType].id ? ImageComponent.Crop : ImageComponent.Input,
                        })
                    );
                }
                if (resetBannerImagesOnFail) {
                    dispatch(setIsCropImageModalVisible(false));
                    dispatch(resetBannerImages(null));
                }

                if (response?.response?.data?.errors) {
                    const { errors } = response.response.data;
                    const { key } = errors[0];
                    dispatch(addMagritteNotification(ERROR_MAPPING[key]));
                } else if (response?.response?.status === 413) {
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormImageTooBigSize));
                } else {
                    if (response?.response?.status !== 502) {
                        Debug.log('error out', 'new load error', {
                            message: response.message,
                            xRequestId: getXRequestId(response),
                        });
                    }
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormImageSomethingWentWrong));
                }
            })
            .finally(() => {
                decrementRequests();
            });
    };
};
